<script setup>
/**
 * FormControlModulecomponentsCategoryColumn
 * Dynamic select dropdown that filters a structural component schema definition.
 * Isolates and alphabetizes associated Many-to-Many entity relationships
 * to act as valid form column selection targets.
 */
import {computed} from 'vue'
import {useComponentsStore} from '@/stores/components'
import {useComponentStructureStore} from '@/stores/componentStructure'

const props = defineProps({
    // Standard Orchestrator Props
    formId: {
        type: String,
        required: true
    },
    field: {
        type: Object,
        required: true
    },
    currentData: {
        type: Object,
        required: true
    },
    value: {
        type: [String, Number, Array],
        default: null
    }
})

// Pinia Stores
const componentsStore = useComponentsStore()
const componentStructureStore = useComponentStructureStore()

// --- Computed ---

/**
 * References the top-level mappings indexing active entity primary IDs.
 */
const componentIds = computed(() => componentsStore.componentIds)

/**
 * Filters and builds the reactive option dataset for the select menu.
 * Automatically alphabetizes the target list by relationship title.
 */
const options = computed(() => {
    const tableName = props.currentData?.tableName
    if (!tableName) {
        return [{value: 0, text: 'Select…'}]
    }

    const componentId = componentIds.value[tableName]
    if (!componentId) {
        return [{value: 0, text: 'Select…'}]
    }

    // Retrieve schema descriptors array from componentStructure store
    const componentData = componentStructureStore.get(componentId) || []

    // Filter down to many-to-many configurations and sort alphabetically
    const filteredRelationships = componentData
        .filter(obj => obj.type === 'relationshipManyToMany')
        .sort((a, b) => (a.title > b.title ? 1 : -1))

    // Construct select menu payload structure
    const dropdownOptions = [{value: 0, text: 'Select…'}]

    filteredRelationships.forEach((obj) => {
        dropdownOptions.push({
            value: obj.id,
            text: obj.title
        })
    })

    return dropdownOptions
})
</script>

<template>
    <select
        :id="field.id"
        v-model="currentData[field.name]"
        :name="field.name"
        :required="field.required"
        class="form-select"
    >
        <option v-for="option in options" :key="option.value" :value="option.value">
            {{ option.text }}
        </option>
    </select>
</template>